/**
 * SiteForm page — create or edit a site.
 *
 * In edit mode, loads the existing site data and pre-fills the form.
 * In create mode, all fields start empty except isActive (defaults true).
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { siteService, type CreateSiteInput, type UpdateSiteInput } from '@/services/siteService';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';

export function SiteForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = !!id;

  const [form, setForm] = useState<CreateSiteInput>({
    siteNumber: '',
    branchName: '',
    address: null,
    city: null,
    state: null,
    divisionCode: null,
    divisionName: null,
    regionCode: null,
    regionName: null,
    realEstateStatus: null,
    leaseStartDate: null,
    leaseExpirationDate: null,
    merakiDeploymentStatus: null,
    isActive: true,
  });

  const [error, setError] = useState<string | null>(null);

  // Load existing site for edit mode
  const { data: siteData, isLoading: loadingSite } = useQuery({
    queryKey: ['site', id],
    queryFn: () => siteService.getByNumber(id!),
    enabled: isEdit,
  });

  useEffect(() => {
    if (siteData?.site) {
      const s = siteData.site;
      setForm({
        siteNumber: s.siteNumber,
        branchName: s.branchName,
        address: s.address,
        city: s.city,
        state: s.state,
        divisionCode: s.divisionCode,
        divisionName: s.divisionName,
        regionCode: s.regionCode,
        regionName: s.regionName,
        realEstateStatus: s.realEstateStatus,
        leaseStartDate: s.leaseStartDate ? s.leaseStartDate.split('T')[0] : null,
        leaseExpirationDate: s.leaseExpirationDate ? s.leaseExpirationDate.split('T')[0] : null,
        merakiDeploymentStatus: s.merakiDeploymentStatus,
        isActive: s.isActive,
      });
    }
  }, [siteData]);

  const createMutation = useMutation({
    mutationFn: (input: CreateSiteInput) => siteService.create(input),
    onSuccess: (site) => {
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      navigate(`/sites/${site.siteNumber}`);
    },
    onError: (err: Error) => setError(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: (input: UpdateSiteInput) => siteService.update(id!, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      queryClient.invalidateQueries({ queryKey: ['site', id] });
      navigate(`/sites/${id}`);
    },
    onError: (err: Error) => setError(err.message),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!form.siteNumber || !form.branchName) {
      setError('Site number and branch name are required.');
      return;
    }

    if (isEdit) {
      const { siteNumber: _, ...updateData } = form;
      updateMutation.mutate(updateData);
    } else {
      createMutation.mutate(form);
    }
  };

  const setField = (field: keyof CreateSiteInput, value: string | boolean | null) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  if (isEdit && loadingSite) return <LoadingSpinner message="Loading site…" />;

  return (
    <div className="max-w-3xl">
      {/* Header */}
      <div className="mb-6">
        <Link to={isEdit ? `/sites/${id}` : '/sites'} className="text-slate-500 no-underline text-sm hover:text-baker-red">
          ← {isEdit ? `Site ${id}` : 'Sites'}
        </Link>
        <h1 className="text-lg font-semibold text-text-primary mt-2">
          {isEdit ? `Edit Site ${id} — ${form.branchName}` : 'Add New Site'}
        </h1>
      </div>

      {error && <ErrorMessage message={error} />}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <Section title="Basic Information">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="Site Number" required>
              <input
                type="text"
                value={form.siteNumber}
                onChange={(e) => setField('siteNumber', e.target.value.replace(/\D/g, '').slice(0, 3))}
                disabled={isEdit}
                placeholder="e.g. 010"
                maxLength={3}
                className="w-full py-2 px-3 rounded-lg border border-border text-sm bg-white disabled:bg-slate-50 disabled:text-slate-500 focus:outline-none focus:ring-2 focus:ring-baker-accent/40 focus:border-baker-accent transition-all"
              />
            </FormField>

            <FormField label="Branch Name" required>
              <input
                type="text"
                value={form.branchName}
                onChange={(e) => setField('branchName', e.target.value)}
                placeholder="e.g. Fort Lauderdale"
                className="w-full py-2 px-3 rounded-lg border border-border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-baker-accent/40 focus:border-baker-accent transition-all"
              />
            </FormField>

            <FormField label="Active">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.isActive ?? true}
                  onChange={(e) => setField('isActive', e.target.checked)}
                  className="w-4 h-4 rounded border-border text-baker-red focus:ring-baker-accent"
                />
                <span className="text-sm text-slate-700">Site is active</span>
              </label>
            </FormField>
          </div>
        </Section>

        {/* Location */}
        <Section title="Location">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="Address" className="md:col-span-2">
              <input
                type="text"
                value={form.address ?? ''}
                onChange={(e) => setField('address', e.target.value || null)}
                placeholder="Street address"
                className="w-full py-2 px-3 rounded-lg border border-border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-baker-accent/40 focus:border-baker-accent transition-all"
              />
            </FormField>

            <FormField label="City">
              <input
                type="text"
                value={form.city ?? ''}
                onChange={(e) => setField('city', e.target.value || null)}
                placeholder="City"
                className="w-full py-2 px-3 rounded-lg border border-border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-baker-accent/40 focus:border-baker-accent transition-all"
              />
            </FormField>

            <FormField label="State">
              <input
                type="text"
                value={form.state ?? ''}
                onChange={(e) => setField('state', e.target.value.toUpperCase().slice(0, 2) || null)}
                placeholder="FL"
                maxLength={2}
                className="w-full py-2 px-3 rounded-lg border border-border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-baker-accent/40 focus:border-baker-accent transition-all"
              />
            </FormField>
          </div>
        </Section>

        {/* Organization */}
        <Section title="Organization">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="Division Code">
              <input
                type="text"
                value={form.divisionCode ?? ''}
                onChange={(e) => setField('divisionCode', e.target.value || null)}
                placeholder="e.g. SE"
                className="w-full py-2 px-3 rounded-lg border border-border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-baker-accent/40 focus:border-baker-accent transition-all"
              />
            </FormField>

            <FormField label="Division Name">
              <input
                type="text"
                value={form.divisionName ?? ''}
                onChange={(e) => setField('divisionName', e.target.value || null)}
                placeholder="e.g. Southeast"
                className="w-full py-2 px-3 rounded-lg border border-border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-baker-accent/40 focus:border-baker-accent transition-all"
              />
            </FormField>

            <FormField label="Region Code">
              <input
                type="text"
                value={form.regionCode ?? ''}
                onChange={(e) => setField('regionCode', e.target.value || null)}
                placeholder="e.g. R01"
                className="w-full py-2 px-3 rounded-lg border border-border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-baker-accent/40 focus:border-baker-accent transition-all"
              />
            </FormField>

            <FormField label="Regional Manager">
              <input
                type="text"
                value={form.regionName ?? ''}
                onChange={(e) => setField('regionName', e.target.value || null)}
                placeholder="Manager name"
                className="w-full py-2 px-3 rounded-lg border border-border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-baker-accent/40 focus:border-baker-accent transition-all"
              />
            </FormField>
          </div>
        </Section>

        {/* Real Estate & Infrastructure */}
        <Section title="Lease Information">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="Lease Start">
              <input
                type="date"
                value={form.leaseStartDate ?? ''}
                onChange={(e) => setField('leaseStartDate', e.target.value || null)}
                className="w-full py-2 px-3 rounded-lg border border-border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-baker-accent/40 focus:border-baker-accent transition-all"
              />
            </FormField>

            <FormField label="Lease Expiration">
              <input
                type="date"
                value={form.leaseExpirationDate ?? ''}
                onChange={(e) => setField('leaseExpirationDate', e.target.value || null)}
                className="w-full py-2 px-3 rounded-lg border border-border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-baker-accent/40 focus:border-baker-accent transition-all"
              />
            </FormField>
          </div>
        </Section>

        {/* Actions */}
        <div className="flex items-center gap-3 pt-4 border-t border-border">
          <button
            type="submit"
            disabled={isSaving}
            className="px-5 py-2.5 rounded-lg bg-baker-red text-white text-sm font-medium cursor-pointer hover:bg-baker-red/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {isSaving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Site'}
          </button>
          <Link
            to={isEdit ? `/sites/${id}` : '/sites'}
            className="px-5 py-2.5 rounded-lg border border-border bg-white text-sm font-medium text-slate-600 no-underline hover:bg-page transition-all"
          >
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="bg-white rounded-lg border border-border p-5">
      <h2 className="text-sm font-semibold text-text-primary mb-4 uppercase tracking-wide">{title}</h2>
      {children}
    </section>
  );
}

function FormField({ label, required, className, children }: { label: string; required?: boolean; className?: string; children: React.ReactNode }) {
  return (
    <div className={className}>
      <label className="block text-xs font-medium text-slate-500 mb-1.5">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}
